import { fixedUi } from '@/i18n/fixedUi';
/**
 * SHEIN query-store-info 响应解析
 * 文档：POST /open-api/openapi-business-backend/query-store-info
 */

const STORE_STATUS_LABELS = {
  0: '停用',
  1: '启用',
};

const SUPPLIER_BUSINESS_MODE_LABELS = {
  'POP-US': '美国 POP 店',
  'POP-GLOBAL': '跨境 POP 店',
  SFS: '商家既要备货又要管理客单',
};

/** 字段中文名（与开放平台文档一致） */
const FIELD_LABELS = {
  storeName: '店铺名称',
  storeStatus: '店铺状态',
  supplierId: '商家 ID',
  supplierBusinessMode: '商家业务类型',
  availableLimit: '可用额度',
  totalLimit: '总额度',
  usedQuota: '已用额度',
};

/** 额度类数值字段（需千分位格式化） */
const QUOTA_NUMBER_FIELDS = new Set(['availableLimit', 'totalLimit', 'usedQuota']);

/** info.storeInfo 字段顺序 */
const STORE_INFO_FIELD_ORDER = [
  'storeName',
  'storeStatus',
  'supplierId',
  'supplierBusinessMode',
];

/** info.storeProductQuota 字段顺序 */
const STORE_QUOTA_FIELD_ORDER = [
  'availableLimit',
  'totalLimit',
  'usedQuota',
];

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 兼容后端已 unwrap 的 info，以及仍带 info 外层的原始响应 */
export function unwrapStoreInfoPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.info && isPlainObject(raw.info)) return raw.info;
  return raw;
}

function formatSupplierBusinessMode(value) {
  if (value === undefined || value === null || value === '') return '—';
  const key = String(value).trim();
  const mapped = SUPPLIER_BUSINESS_MODE_LABELS[key];
  return mapped ? `${mapped}（${key}）` : key;
}

export function formatStoreInfoValue(key, value) {
  if (value === undefined || value === null || value === '') return '—';

  if (key === 'storeStatus') {
    return STORE_STATUS_LABELS[value] ?? STORE_STATUS_LABELS[String(value)] ?? String(value);
  }

  if (key === 'supplierBusinessMode') {
    return formatSupplierBusinessMode(value);
  }

  if (key === 'supplierId') {
    return String(value);
  }

  if (QUOTA_NUMBER_FIELDS.has(key)) {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) {
      return num.toLocaleString('zh-CN');
    }
  }

  if (typeof value === 'boolean') {
    return value ? fixedUi("是") : fixedUi("否");
  }

  return String(value);
}

function pickSectionRows(source, fieldOrder) {
  return fieldOrder.map((key) => ({
    key,
    label: FIELD_LABELS[key] || key,
    value: formatStoreInfoValue(key, isPlainObject(source) ? source[key] : undefined),
  }));
}

function readNested(payload, key) {
  if (!payload) return null;
  if (isPlainObject(payload[key])) return payload[key];
  return null;
}

/** 取出文档定义的 storeInfo 对象 */
export function pickStoreInfoNode(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return null;
  const nested = readNested(payload, 'storeInfo');
  if (nested) return nested;
  // 兼容直接落了扁平字段的缓存
  if (
    Object.prototype.hasOwnProperty.call(payload, 'storeName')
    || Object.prototype.hasOwnProperty.call(payload, 'supplierId')
    || Object.prototype.hasOwnProperty.call(payload, 'storeStatus')
  ) {
    return payload;
  }
  return null;
}

/** 取出文档定义的 storeProductQuota 对象 */
export function pickStoreQuotaNode(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return null;
  return readNested(payload, 'storeProductQuota');
}

export function getStoreStatusMeta(raw) {
  const node = pickStoreInfoNode(raw);
  const code = node?.storeStatus;
  if (code === undefined || code === null || code === '') {
    return { code: null, label: '—', modifier: 'offline' };
  }
  const label = formatStoreInfoValue('storeStatus', code);
  const numeric = Number(code);
  if (numeric === 1) return { code, label, modifier: 'success' };
  if (numeric === 0) return { code, label, modifier: 'failed' };
  return { code, label, modifier: 'pending' };
}

export function getStoreDisplayName(raw, fallback = '') {
  const node = pickStoreInfoNode(raw);
  const name = node?.storeName;
  if (name !== undefined && name !== null && String(name).trim() !== '') {
    return String(name).trim();
  }
  return fallback || '—';
}

export function getStoreSupplierId(raw) {
  const node = pickStoreInfoNode(raw);
  const id = node?.supplierId;
  if (id === undefined || id === null || id === '') return '';
  return String(id);
}

export function buildStoreBasicRows(raw) {
  return pickSectionRows(pickStoreInfoNode(raw), STORE_INFO_FIELD_ORDER);
}

export function buildStoreQuotaRows(raw) {
  return pickSectionRows(pickStoreQuotaNode(raw), STORE_QUOTA_FIELD_ORDER);
}

/**
 * 将 query-store-info 响应拆成文档定义的两个区块：
 * - storeInfo：店铺信息
 * - storeProductQuota：店铺发品可用额度
 */
export function buildStoreInfoSections(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return [];

  const sections = [];
  const basicRows = buildStoreBasicRows(payload);
  if (basicRows.some((row) => row.value !== '—') || pickStoreInfoNode(payload)) {
    sections.push({ id: 'storeInfo', title: '基本信息', rows: basicRows });
  }

  if (pickStoreQuotaNode(payload)) {
    sections.push({
      id: 'storeProductQuota',
      title: '店铺发品可用额度',
      rows: buildStoreQuotaRows(payload),
    });
  }

  return sections;
}
